import React from "react";
import { CheckCircle, XCircle, ArrowsLeftRight } from "@phosphor-icons/react";
import { fmtMoney, fmtRPM } from "@/lib/defaults";
import { useLandingT } from "@/lib/landingI18n";

const LOAD_A = {
  route: "Louisville, KY → Nashville, TN",
  commodity: "Dry Foods",
  rate: 1400,
  total: 205,
  rpm: 6.83,
  trip: 328,
  driver: 133.25,
  fee: 140,
  net: 798.75,
  ppm: 3.9,
  profitable: true,
};
const LOAD_B = {
  route: "Danville, KY → Charlotte, NC",
  commodity: "Dry Foods",
  rate: 1100,
  total: 431,
  rpm: 2.55,
  trip: 689.6,
  driver: 280.15,
  fee: 110,
  net: 20.25,
  ppm: 0.05,
  profitable: false,
};

const Metric = ({ label, value, accent }) => (
  <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0">
    <span className="text-[10px] uppercase tracking-[0.12em] text-slate-400">
      {label}
    </span>
    <span className={`tabular text-sm font-medium ${accent}`}>{value}</span>
  </div>
);

const LoadColumn = ({ label, load, tone }) => {
  const isP = load.profitable;
  return (
    <div className="glass rounded-2xl p-5 md:p-6 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-16 -end-16 w-40 h-40 rounded-full blur-3xl opacity-30"
        style={{
          background: isP
            ? "radial-gradient(closest-side, rgba(16,185,129,0.7), transparent 70%)"
            : "radial-gradient(closest-side, rgba(239,68,68,0.6), transparent 70%)",
        }}
      />
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400">
          {label}
        </span>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] font-semibold border ${
            isP
              ? "bg-emerald-500/12 text-emerald-300 border-emerald-500/30"
              : "bg-red-500/12 text-red-300 border-red-500/30"
          }`}
        >
          {isP ? (
            <CheckCircle size={12} weight="fill" />
          ) : (
            <XCircle size={12} weight="fill" />
          )}
          {isP ? "Profitable" : "Not profitable"}
        </span>
      </div>

      <p className="mt-4 text-white text-base font-semibold leading-tight">
        {load.route}
      </p>
      <p className="text-slate-500 text-xs mt-1">{load.commodity}</p>

      <div className="mt-4 rounded-xl border border-white/8 bg-white/[0.02] px-3 py-1">
        <Metric label="Rate" value={fmtMoney(load.rate)} accent="text-white" />
        <Metric label="Total Miles" value={load.total.toLocaleString()} accent="text-white" />
        <Metric label="Real RPM" value={fmtRPM(load.rpm)} accent="text-white" />
        <Metric label="Trip Cost" value={fmtMoney(load.trip)} accent="text-red-300" />
        <Metric label="Driver Pay" value={fmtMoney(load.driver)} accent="text-red-300" />
        <Metric label="Dispatcher Fee" value={fmtMoney(load.fee)} accent="text-red-300" />
      </div>

      <div className="mt-4 flex items-end justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400">
            Net Profit
          </p>
          <p
            className={`tabular text-3xl font-semibold mt-1 ${
              isP ? "neon-text" : "crimson-text"
            }`}
          >
            {fmtMoney(load.net)}
          </p>
        </div>
        <div className="text-end">
          <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400">
            Profit/Mile
          </p>
          <p className="tabular text-lg text-white font-semibold mt-1">
            {fmtRPM(load.ppm)}
          </p>
        </div>
      </div>
    </div>
  );
};

export const CompareSection = ({ lang }) => {
  const t = useLandingT(lang);
  return (
    <section className="relative py-20 md:py-28 border-t border-white/8">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <div className="kicker">
              <span className="dot" />
              {t("compareKicker")}
            </div>
            <h2 className="h-section mt-5 text-white">{t("compareTitle")}</h2>
            <p className="mt-4 text-slate-400 text-base leading-relaxed">
              {t("compareBody")}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-emerald-300 text-sm">
              <ArrowsLeftRight size={16} weight="bold" />
              Load A vs Load B
            </div>
          </div>

          <div className="lg:col-span-8 grid md:grid-cols-2 gap-4">
            <LoadColumn label="Load A" load={LOAD_A} />
            <LoadColumn label="Load B" load={LOAD_B} />
          </div>
        </div>
      </div>
    </section>
  );
};
